const IST = "Asia/Kolkata";

/**
 * DB/API timestamps are UTC instants. Naive ISO strings (no Z/offset) must be
 * parsed as UTC — otherwise the browser treats them as local time and IST
 * conversion in tooltips appears unchanged.
 */
export function parseAuditTimestamp(value: string | Date): Date {
  if (value instanceof Date) return value;

  const trimmed = value.trim();
  if (!trimmed) return new Date(Number.NaN);

  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return new Date(trimmed);
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    return new Date(`${trimmed}Z`);
  }

  return new Date(trimmed);
}

/** Compact UTC label for visible table text (no suffix). */
export function formatAuditVisibleUtc(value: string | Date): string {
  const d = parseAuditTimestamp(value);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function formatDateTimeIST(value: string | Date): string {
  const d = parseAuditTimestamp(value);
  return (
    new Intl.DateTimeFormat("en-IN", {
      timeZone: IST,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d) + " IST"
  );
}

export function formatDateIST(value: string | Date): string {
  const d = parseAuditTimestamp(value);
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

export function formatDateTimeUTC(value: string | Date): string {
  const d = parseAuditTimestamp(value);
  return (
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d) + " UTC"
  );
}

/** Date-only UTC label for visible range text. */
export function formatAuditVisibleDateUtc(value: string | Date): string {
  const d = parseAuditTimestamp(value);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(d);
}
